// ─── Payload Builder Types ───────────────────────────────────────────────────

/** Per-item data contributed by each Web Component instance. */
export type PayloadItemData = Record<string, unknown>;

export type PayloadBody = FormData | Record<string, unknown>;

/** A single entry in a batch, combining the ID with its item-specific data. */
export interface PayloadItem {
    id: string;
    itemData?: PayloadItemData;
}

/**
 * Function provided by each Web Component subclass.
 * Receives all batched items and returns the body to POST to the API.
 */
export type PayloadBuilder = (items: PayloadItem[]) => PayloadBody;

// ─── Request & Queue Interfaces ──────────────────────────────────────────────

export interface RequestOptions {
    id: string;
    usecase: string;
    url: string;
    cacheStrategy: string;
    /** Grouping key for batching compatibility (same queue only if equal). */
    batchKey?: string;
    /** Per-item key for cache/inflight identity (payload-sensitive). */
    cacheKey?: string;
    /** Per-item data contributed by this specific component instance (e.g. quantity, variant). */
    itemData?: PayloadItemData;
    /** Builds the POST body from all batched items. Must be consistent for all requests in the same queue. */
    buildPayload: PayloadBuilder;
}

export interface PromiseCallbacks {
    resolve: (value: unknown) => void;
    reject: (reason: unknown) => void;
}

/** Internal per-item queue entry, holding itemData alongside the promise callbacks. */
export interface QueueEntry {
    id: string;
    cacheKey?: string;
    itemData?: PayloadItemData;
    callbacks: PromiseCallbacks[];
}

export interface Queue {
    url: string;
    usecase: string;
    batchKey?: string;
    cacheStrategy: string;
    /** Keyed by item ID. */
    items: Map<string, QueueEntry>;
    /** Payload builder sourced from the first requestContent call for this queue. */
    buildPayload: PayloadBuilder;
    timeoutId: ReturnType<typeof setTimeout> | null;
}

export interface MemoryCacheEntry {
    data: unknown;
    expireAt: number | null;
}

export interface ApiResponseResult {
    id: string;
    [key: string]: unknown;
}

export interface ApiResponse {
    usecase: string;
    length: number;
    results: ApiResponseResult[];
}

interface SessionCacheEnvelope {
    __acm: true;
    createdAt: number;
    data: unknown;
}

export interface AsyncContentManagerConfig {
    debounceMs: number;
    maxBatchSize: number;
    maxConcurrentRequests: number;
}

export type AsyncContentManagerConfigOverrides = Partial<AsyncContentManagerConfig>;

// ─── AsyncContentManager ─────────────────────────────────────────────────────

const DEFAULT_ASYNC_CONTENT_MANAGER_CONFIG: AsyncContentManagerConfig = {
    debounceMs: 50,
    maxBatchSize: 40,
    maxConcurrentRequests: 3,
};

export class AsyncContentManager {
    private static instance: AsyncContentManager | null = null;
    private static runtimeConfig: AsyncContentManagerConfig = { ...DEFAULT_ASYNC_CONTENT_MANAGER_CONFIG };

    private memoryCache = new Map<string, MemoryCacheEntry>();
    private queues = new Map<string, Queue>();

    /** In-flight promise sharing: keyed by `url::usecase::id` */
    private inflightPromises = new Map<string, Promise<unknown>>();

    /** Number of currently active HTTP requests */
    private activeRequests = 0;

    /** Queue of chunk-executor functions waiting for a concurrency slot */
    private pendingChunks: Array<() => Promise<void>> = [];

    private config: AsyncContentManagerConfig;

    private constructor() {
        this.config = { ...AsyncContentManager.runtimeConfig };
    }

    private _toURLSearchParams(payload: PayloadBody): URLSearchParams {
        const searchParams = new URLSearchParams();

        if (payload instanceof FormData) {
            payload.forEach((value, key) => {
                searchParams.append(key, value as string);
            });
            return searchParams;
        }

        Object.entries(payload).forEach(([key, value]) => {
            if (value === undefined || value === null) {
                searchParams.append(key, '');
                return;
            }

            if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                searchParams.append(key, String(value));
                return;
            }

            searchParams.append(key, JSON.stringify(value));
        });

        return searchParams;
    }

    public static getInstance(): AsyncContentManager {
        if (!AsyncContentManager.instance) {
            AsyncContentManager.instance = new AsyncContentManager();
        }
        return AsyncContentManager.instance;
    }

    /**
     * Overrides debounce/batch/concurrency settings for future queue processing.
     */
    public static configure(overrides: AsyncContentManagerConfigOverrides): void {
        const nextConfig = AsyncContentManager._normalizeConfig({
            ...AsyncContentManager.runtimeConfig,
            ...overrides,
        });

        AsyncContentManager.runtimeConfig = nextConfig;

        if (AsyncContentManager.instance) {
            AsyncContentManager.instance.config = { ...nextConfig };
        }
    }

    public static getConfig(): AsyncContentManagerConfig {
        if (AsyncContentManager.instance) {
            return { ...AsyncContentManager.instance.config };
        }
        return { ...AsyncContentManager.runtimeConfig };
    }

    private static _normalizeConfig(config: AsyncContentManagerConfig): AsyncContentManagerConfig {
        return {
            debounceMs: AsyncContentManager._toPositiveInteger(
                config.debounceMs,
                DEFAULT_ASYNC_CONTENT_MANAGER_CONFIG.debounceMs,
                0,
            ),
            maxBatchSize: AsyncContentManager._toPositiveInteger(
                config.maxBatchSize,
                DEFAULT_ASYNC_CONTENT_MANAGER_CONFIG.maxBatchSize,
                1,
            ),
            maxConcurrentRequests: AsyncContentManager._toPositiveInteger(
                config.maxConcurrentRequests,
                DEFAULT_ASYNC_CONTENT_MANAGER_CONFIG.maxConcurrentRequests,
                1,
            ),
        };
    }

    private static _toPositiveInteger(value: unknown, fallback: number, min: number): number {
        if (typeof value !== 'number' || Number.isNaN(value)) {
            return fallback;
        }

        const normalized = Math.floor(value);
        return normalized < min ? min : normalized;
    }

    /**
     * Requests structured JSON data for a Web Component.
     * Applies client-side caching, in-flight deduplication, and batched fetching.
     *
     * The caller supplies `itemData` (component-specific per-item parameters) and
     * `buildPayload` (a function that assembles the POST body from all batched items).
     * The manager itself remains agnostic of the actual payload shape.
     */
    public async requestContent({
        id,
        usecase,
        url,
        cacheStrategy,
        batchKey,
        cacheKey,
        itemData,
        buildPayload,
    }: RequestOptions): Promise<unknown> {
        // 1. Client-side cache hit
        const cachedData = this._getCache(usecase, id, batchKey, cacheKey, cacheStrategy);
        if (cachedData !== null) {
            return cachedData;
        }

        // 2. In-flight deduplication: reuse a running promise for the same ID
        const inflightKey = `${url}::${usecase}::${batchKey ?? ''}::${id}::${cacheKey ?? ''}`;
        if (this.inflightPromises.has(inflightKey)) {
            return this.inflightPromises.get(inflightKey)!;
        }

        // 3. Add to the batching queue
        const queueKey = `${url}::${usecase}::${batchKey ?? ''}`;
        if (!this.queues.has(queueKey)) {
            this.queues.set(queueKey, {
                url,
                usecase,
                batchKey,
                cacheStrategy,
                items: new Map<string, QueueEntry>(),
                buildPayload,
                timeoutId: null,
            });
        }

        const queue = this.queues.get(queueKey)!;
        const itemKey = `${id}::${cacheKey ?? ''}`;

        const promise = new Promise<unknown>((resolve, reject) => {
            if (!queue.items.has(itemKey)) {
                queue.items.set(itemKey, { id, cacheKey, itemData, callbacks: [] });
            }
            queue.items.get(itemKey)!.callbacks.push({ resolve, reject });

            // Start debounce timer on first insertion
            if (queue.timeoutId === null) {
                queue.timeoutId = setTimeout(() => this._flushQueue(queueKey), this.config.debounceMs);
            }
        });

        // Register as in-flight so later callers can attach to the same promise
        this.inflightPromises.set(inflightKey, promise);

        // Clean up the inflight entry once settled
        promise.finally(() => {
            this.inflightPromises.delete(inflightKey);
        });

        return promise;
    }

    /**
     * Dequeues all collected items, splits them into chunks respecting
     * maxBatchSize, and schedules each chunk respecting maxConcurrentRequests.
     */
    private _flushQueue(queueKey: string): void {
        const queue = this.queues.get(queueKey);
        if (!queue) return;
        this.queues.delete(queueKey);

        const entries = Array.from(queue.items.entries());
        if (entries.length === 0) return;

        // Split into chunks of maxBatchSize
        for (let i = 0; i < entries.length; i += this.config.maxBatchSize) {
            const chunkEntries = entries.slice(i, i + this.config.maxBatchSize);
            const chunkItems = new Map<string, QueueEntry>(chunkEntries);
            const chunkIds = chunkEntries.map(([, entry]) => entry.id);
            const chunkExecutor = () => this._sendChunk(queue, chunkIds, chunkItems);
            this._scheduleChunk(chunkExecutor);
        }
    }

    /**
     * Schedules a chunk executor, respecting the concurrent request limit.
     */
    private _scheduleChunk(executor: () => Promise<void>): void {
        if (this.activeRequests < this.config.maxConcurrentRequests) {
            this.activeRequests++;
            executor().finally(() => {
                this.activeRequests--;
                this._drainPendingChunks();
            });
        } else {
            this.pendingChunks.push(executor);
        }
    }

    /**
     * Starts the next pending chunk if a concurrency slot is available.
     */
    private _drainPendingChunks(): void {
        while (this.activeRequests < this.config.maxConcurrentRequests && this.pendingChunks.length > 0) {
            const next = this.pendingChunks.shift()!;
            this.activeRequests++;
            next().finally(() => {
                this.activeRequests--;
                this._drainPendingChunks();
            });
        }
    }

    /**
     * Sends a single batch chunk to the API and distributes results to waiting callbacks.
     * The request body is assembled entirely by the caller-supplied `buildPayload` function —
     * the manager has no knowledge of the payload structure beyond converting it to FormData.
     */
    private async _sendChunk(queue: Queue, ids: string[], items: Map<string, QueueEntry>): Promise<void> {
        try {
            // Delegate payload construction entirely to the usecase-specific builder
            const payloadItems: PayloadItem[] = Array.from(items.values()).map((entry) => ({
                id: entry.id,
                itemData: entry.itemData,
            }));
            const body = this._toURLSearchParams(queue.buildPayload(payloadItems));

            const response = await fetch(queue.url, {
                method: 'POST',
                body,
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const contentType = response.headers.get('content-type') ?? '';

            if (contentType.includes('application/json')) {
                const data: ApiResponse = await response.json();

                for (const result of data.results ?? []) {
                    const { id, ...resultData } = result;

                    const matchingItemEntry = Array.from(items.entries()).find(([, entry]) => entry.id === id);
                    if (!matchingItemEntry) {
                        continue;
                    }

                    const [itemKey, entry] = matchingItemEntry;

                    this._setCache(queue.usecase, id, queue.batchKey, entry.cacheKey, resultData, queue.cacheStrategy);

                    if (entry) {
                        entry.callbacks.forEach((cb) => cb.resolve(resultData));
                        items.delete(itemKey);
                    }
                }

                // Reject callbacks for IDs with no matching result in the response
                items.forEach((entry, itemKey) => {
                    console.warn('[AsyncContentManager] missing result for requested id', {
                        id: entry.id,
                        itemKey,
                        cacheKey: entry.cacheKey,
                        callbackCount: entry.callbacks.length,
                    });
                    entry.callbacks.forEach((cb) => cb.reject(new Error('Keine Daten geliefert')));
                });

                return;
            }

            // Non-JSON (e.g. text/html) response: the API returns one HTML body per
            // request rather than a per-id `results` array, so it cannot be split
            // across multiple batched ids. Broadcast the body to every queued item.
            const html = await response.text();
            if (items.size > 1) {
                console.warn(
                    '[AsyncContentManager] HTML response cannot be split across multiple ids, broadcasting to all',
                    {
                        ids,
                    },
                );
            }

            items.forEach((entry, itemKey) => {
                const data = { dataAsHtml: html };

                this._setCache(queue.usecase, entry.id, queue.batchKey, entry.cacheKey, data, queue.cacheStrategy);
                entry.callbacks.forEach((cb) => cb.resolve(data));
                items.delete(itemKey);
            });
        } catch (error) {
            console.error('[AsyncContentManager] chunk request failed', {
                url: queue.url,
                usecase: queue.usecase,
                ids,
                error,
            });
            items.forEach((entry) => {
                entry.callbacks.forEach((cb) => cb.reject(error));
            });
        }
    }

    // ─── Cache helpers ─────────────────────────────────────────────────────────

    private _getCacheKey(usecase: string, id: string, batchKey?: string, cacheKey?: string): string {
        return `${usecase}::${batchKey ?? ''}::${id}::${cacheKey ?? ''}`;
    }

    private _getCache(
        usecase: string,
        id: string,
        batchKey: string | undefined,
        cacheKey: string | undefined,
        strategy: string,
    ): unknown | null {
        if (strategy === 'none') return null;

        const key = this._getCacheKey(usecase, id, batchKey, cacheKey);

        if (strategy === 'session') {
            try {
                const stored = sessionStorage.getItem(key);
                if (!stored) return null;

                const parsed = JSON.parse(stored) as unknown;
                if (this._isSessionCacheEnvelope(parsed)) {
                    return parsed.data;
                }

                // Backward compatibility for previously stored plain JSON payloads.
                return parsed;
            } catch {
                return null;
            }
        }

        if (this.memoryCache.has(key)) {
            const entry = this.memoryCache.get(key)!;
            if (entry.expireAt !== null && entry.expireAt < Date.now()) {
                this.memoryCache.delete(key);
                return null;
            }
            return entry.data;
        }

        return null;
    }

    private _setCache(
        usecase: string,
        id: string,
        batchKey: string | undefined,
        cacheKey: string | undefined,
        data: unknown,
        strategy: string,
    ): void {
        if (strategy === 'none') return;

        const key = this._getCacheKey(usecase, id, batchKey, cacheKey);

        if (strategy === 'session') {
            const payload = JSON.stringify({
                __acm: true,
                createdAt: Date.now(),
                data,
            } as SessionCacheEnvelope);

            try {
                sessionStorage.setItem(key, payload);
            } catch (error) {
                if (!this._isQuotaExceededError(error)) {
                    throw error;
                }

                const requiredChars = key.length + payload.length;
                this._evictOldSessionEntries(requiredChars, key);

                try {
                    sessionStorage.setItem(key, payload);
                } catch (retryError) {
                    console.warn('[AsyncContentManager] session cache write skipped after eviction', {
                        key,
                        error: retryError,
                    });
                }
            }

            return;
        }

        let expireAt: number | null = null;
        const ttlSeconds = parseInt(strategy, 10);
        if (!isNaN(ttlSeconds)) {
            expireAt = Date.now() + ttlSeconds * 1000;
        }

        // 'global' or unrecognised values → memory cache without TTL
        this.memoryCache.set(key, { data, expireAt });
    }

    private _isSessionCacheEnvelope(value: unknown): value is SessionCacheEnvelope {
        if (!value || typeof value !== 'object') return false;
        if (!('__acm' in value) || !('createdAt' in value) || !('data' in value)) return false;

        const envelope = value as Record<string, unknown>;
        return envelope.__acm === true && typeof envelope.createdAt === 'number';
    }

    private _isQuotaExceededError(error: unknown): boolean {
        if (!(error instanceof DOMException)) return false;
        return (
            error.name === 'QuotaExceededError' ||
            error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
            error.code === 22 ||
            error.code === 1014
        );
    }

    private _evictOldSessionEntries(requiredChars: number, keepKey: string): void {
        const candidates: Array<{ key: string; size: number; createdAt: number }> = [];

        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (!key || key === keepKey) continue;

            const rawValue = sessionStorage.getItem(key);
            if (rawValue === null) continue;

            try {
                const parsed = JSON.parse(rawValue) as unknown;
                if (!this._isSessionCacheEnvelope(parsed)) continue;

                candidates.push({
                    key,
                    size: key.length + rawValue.length,
                    createdAt: parsed.createdAt,
                });
            } catch {
                continue;
            }
        }

        candidates.sort((a, b) => a.createdAt - b.createdAt);

        let freed = 0;
        for (const entry of candidates) {
            sessionStorage.removeItem(entry.key);
            freed += entry.size;
            if (freed >= requiredChars) {
                break;
            }
        }
    }
}
